import { useCallback } from "react";

import { useUpdateFernieSuggestionsOnMutation } from "~/gql/mutations/tenant.generated";
import snackMessages from "~/helpers/snackMessages";
import useMutationRunner from "~/hooks/useMutationRunner";
import { useSwitchTenantMutation } from "~/services/adminApi";

const ENTITY_LABEL = "tenant";

export interface UseTenantMutationsReturn {
	switchTenant: (tenantId: string) => Promise<boolean>;
	updateFernieSuggestionsOn: (next: boolean) => Promise<boolean>;
}

// SwitchTenant is served only by the admin GraphQL endpoint, so its trigger comes from adminApi instead of apiEndpoints.
const useTenantMutations = (): UseTenantMutationsReturn => {
	const { runForEntity } = useMutationRunner();
	const [switchTenantMutation] = useSwitchTenantMutation();
	const [updateFernieSuggestionsOnMutation] = useUpdateFernieSuggestionsOnMutation();

	const switchTenant = useCallback(
		async (tenantId: string): Promise<boolean> => {
			const switched = await runForEntity(
				switchTenantMutation({ tenantId }),
				(data) => data,
				(payload) => (payload.success ? payload : null),
				snackMessages.actionError(`switching ${ENTITY_LABEL}`)
			);
			return Boolean(switched);
		},
		[runForEntity, switchTenantMutation]
	);

	const updateFernieSuggestionsOn = useCallback(
		async (next: boolean): Promise<boolean> => {
			const savedTenant = await runForEntity(
				updateFernieSuggestionsOnMutation({ input: { fernieSuggestionsOn: next } }),
				(result) => result.updateFernieSuggestionsOn,
				(payload) => payload.tenant,
				snackMessages.fernieSuggestionsSaveError
			);
			return Boolean(savedTenant);
		},
		[runForEntity, updateFernieSuggestionsOnMutation]
	);

	return { switchTenant, updateFernieSuggestionsOn };
};

export default useTenantMutations;
